/**
 * OnboardingWizard - Main onboarding flow for organizations
 * Provides two paths: Create or Join organization
 */
import React, { useState } from 'react';
import { CreateOrganizationForm } from './CreateOrganizationForm';
import { JoinOrganizationForm } from './JoinOrganizationForm';
import { LoadingSpinner } from '../common/LoadingSpinner';

type OnboardingStep = 'choice' | 'create' | 'join' | 'success';

interface OnboardingWizardProps {
  onComplete?: () => void;
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('choice');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleCreateSuccess = (orgName: string) => {
    setOrganizationName(orgName);
    setCurrentStep('success');
    // Auto-redirect after success
    setTimeout(() => {
      onComplete?.();
    }, 2000);
  };

  const handleJoinSuccess = (orgName: string) => {
    setOrganizationName(orgName);
    setCurrentStep('success');
    // Auto-redirect after success
    setTimeout(() => {
      onComplete?.();
    }, 2000);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'choice':
        return (
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome to FinStack
              </h1>
              <p className="text-gray-600">
                To get started, you'll need to either create a new organization or join an existing one.
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => setCurrentStep('create')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create New Organization
              </button>

              <button
                onClick={() => setCurrentStep('join')}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                Join Existing Organization
              </button>
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                Don't have an invite code? Contact your organization administrator.
              </p>
            </div>
          </div>
        );

      case 'create':
        return (
          <div className="max-w-md mx-auto">
            <div className="mb-6">
              <button
                onClick={() => setCurrentStep('choice')}
                className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Create Organization</h1>
              <p className="text-gray-600 mt-2">Set up your new organization to get started.</p>
            </div>
            
            <CreateOrganizationForm
              onSuccess={handleCreateSuccess}
              loading={loading}
              setLoading={setLoading}
            />
          </div>
        );

      case 'join':
        return (
          <div className="max-w-md mx-auto">
            <div className="mb-6">
              <button
                onClick={() => setCurrentStep('choice')}
                className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Join Organization</h1>
              <p className="text-gray-600 mt-2">Enter your invite code to join an existing organization.</p>
            </div>
            
            <JoinOrganizationForm
              onSuccess={handleJoinSuccess}
              loading={loading}
              setLoading={setLoading}
            />
          </div>
        );

      case 'success':
        return (
          <div className="max-w-md mx-auto text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to {organizationName}!
              </h1>
              <p className="text-gray-600">
                Your organization is ready. Redirecting to your dashboard...
              </p>
            </div>
            
            <div className="flex justify-center">
              <LoadingSpinner size="medium" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-2xl">
        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              currentStep === 'choice' ? 'bg-blue-600' : 'bg-gray-300'
            }`} />
            <div className={`w-8 h-1 ${
              ['create', 'join', 'success'].includes(currentStep) ? 'bg-blue-600' : 'bg-gray-300'
            }`} />
            <div className={`w-3 h-3 rounded-full ${
              ['create', 'join'].includes(currentStep) ? 'bg-blue-600' : 'bg-gray-300'
            }`} />
            <div className={`w-8 h-1 ${
              currentStep === 'success' ? 'bg-blue-600' : 'bg-gray-300'
            }`} />
            <div className={`w-3 h-3 rounded-full ${
              currentStep === 'success' ? 'bg-blue-600' : 'bg-gray-300'
            }`} />
          </div>
        </div>

        {/* Step content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};